import React from "react";
import { Link } from "react-router-dom";
import {
  Music,
  Shield,
  Lock,
  Info,
  ExternalLink,
  Mail,
  MapPin,
  CheckCircle2,
  Calendar,
  Layers,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

export default function PianoPrivacyPolicy() {
  const sections = [
    { id: "info-we-collect", title: "1. Information We Collect" },
    { id: "automatically-collected", title: "2. Automatically Collected Information" },
    { id: "advertising", title: "3. Advertising" },
    { id: "analytics", title: "4. Analytics and Crash Reporting" },
    { id: "permissions", title: "5. Permissions" },
    { id: "piano-features", title: "6. Piano, Music and Audio Features" },
    { id: "childrens-privacy", title: "7. Children's Privacy" },
    { id: "third-party-services", title: "8. Third-Party Services" },
    { id: "how-info-used", title: "9. How Information Is Used" },
    { id: "data-sharing", title: "10. Data Sharing" },
    { id: "data-retention", title: "11. Data Retention" },
    { id: "data-security", title: "12. Data Security" },
    { id: "privacy-choices", title: "13. Your Privacy Choices" },
    { id: "data-deletion", title: "14. Data Deletion" },
    { id: "international-processing", title: "15. International Processing" },
    { id: "policy-changes", title: "16. Changes to This Privacy Policy" },
    { id: "contact-us", title: "17. Contact Us" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 antialiased selection:bg-indigo-500 selection:text-white">
      {/* Navigation bar */}
      <nav className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition-colors hover:text-indigo-600"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
              <Music className="h-4 w-4" />
            </span>
            <span className="hidden text-sm font-bold text-slate-900 sm:inline">
              Piano: Keyboard, Learn &amp; Songs
            </span>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 py-14 text-white shadow-xl">
        <div className="pointer-events-none absolute inset-0 opacity-10">
          <div className="absolute -left-12 -top-12 h-64 w-64 rounded-full bg-indigo-500 blur-3xl"></div>
          <div className="absolute right-0 top-1/2 h-80 w-80 rounded-full bg-purple-500 blur-3xl"></div>
        </div>

        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-indigo-400/30 bg-indigo-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-300">
            <Shield className="h-3.5 w-3.5 text-indigo-400" />
            Official Privacy Documentation
          </div>

          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-2 text-lg font-medium text-indigo-200 sm:text-xl">
            Piano: Keyboard, Learn &amp; Songs
          </p>

          {/* Dates metadata pills */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/80 px-3.5 py-1.5 text-slate-300">
              <Calendar className="h-4 w-4 text-indigo-400" />
              <strong>Effective Date:</strong> October 2, 2026
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800/80 px-3.5 py-1.5 text-slate-300">
              <RefreshCw className="h-4 w-4 text-emerald-400" />
              <strong>Last Updated:</strong> October 2, 2026
            </span>
          </div>
        </div>
      </header>

      {/* Main Content Layout */}
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        {/* Intro Card */}
        <section className="mb-8 overflow-hidden rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-start gap-4">
            <div className="hidden rounded-xl bg-indigo-50 p-3 text-indigo-600 sm:block">
              <Info className="h-6 w-6" />
            </div>
            <div className="space-y-3 leading-relaxed text-slate-600">
              <p className="text-base sm:text-lg text-slate-700">
                This Privacy Policy explains how <strong>Piano: Keyboard, Learn &amp; Songs</strong> (&ldquo;the App,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) handles information when you use our mobile application.
              </p>
              <p>
                We respect your privacy and are committed to protecting it. This Privacy Policy describes the information that may be collected, how it may be used, and the choices available to users.
              </p>
            </div>
          </div>

          {/* Feature Highlights Grid */}
          <div className="mt-6 grid grid-cols-1 gap-4 border-t border-slate-100 pt-6 sm:grid-cols-3">
            <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                <Music className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-semibold text-slate-800">Local Processing</p>
                <p className="text-slate-500">Audio &amp; keys stay on device</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                <Lock className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-semibold text-slate-800">Device ID Only</p>
                <p className="text-slate-500">No accounts, PII, or media saved</p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                <Shield className="h-5 w-5" />
              </div>
              <div className="text-xs">
                <p className="font-semibold text-slate-800">Data Protection</p>
                <p className="text-slate-500">We do not sell personal data</p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Jump Navigation */}
        <section className="mb-10 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-500">
            <Layers className="h-4 w-4 text-indigo-500" /> Table of Contents
          </h2>
          <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
            {sections.map((sec) => (
              <a
                key={sec.id}
                href={`#${sec.id}`}
                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 font-medium text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600"
              >
                {sec.title}
              </a>
            ))}
          </div>
        </section>

        {/* Policy Detailed Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <article
            id="info-we-collect"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                1
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Information We Collect
              </h2>
            </div>
            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                The App is designed primarily to provide a full-featured virtual piano keyboard, piano learning, songs, musical instruments (Grand Piano, Saxophone, Acoustic Guitar, Flute, Strings, Synth, Music Box), a standalone Cyber Drum Stage with 40 percussion pads, 16-step beat sequencer, studio metronome, and related interactive music features.
              </p>
              
              {/* Device ID Only Clarification Callout */}
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/80 p-4.5 text-indigo-950">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-indigo-600 shrink-0" />
                  <p className="font-bold text-indigo-950">
                    Important Privacy Principle: Device Identifier Only
                  </p>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-indigo-900">
                  We respect user privacy above all else. <strong>We may only and only collect a Device ID</strong> (such as an anonymous hardware/operating system identifier or advertising ID) strictly needed for technical stability, app integrity, crash prevention, and ad mediation. We do <strong>not</strong> collect names, phone numbers, postal addresses, contact lists, photos, or microphone recordings.
                </p>
              </div>

              <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4 text-emerald-900">
                <p className="font-semibold text-emerald-950">
                  We do not require users to create an account or sign in to use the App.
                </p>
              </div>

              <div className="pt-2">
                <h3 className="mb-2 text-lg font-semibold text-slate-800">
                  Information You Provide
                </h3>
                <p className="mb-3">
                  Because the App does not offer account registration, cloud profile storage, or user data-submission forms, we do not directly request or store personal identity data for normal use of the App.
                </p>
                <p>
                  If you contact us voluntarily for customer support, bug reporting, or feedback, we will only receive the information you provide in that correspondence (such as your email address and message content) solely to assist you and resolve your inquiry.
                </p>
              </div>
            </div>
          </article>

          {/* Section 2 */}
          <article
            id="automatically-collected"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                2
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Automatically Collected Information
              </h2>
            </div>
            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                Depending on the services and SDKs integrated into the App (such as Google AdMob or Firebase services), certain technical diagnostic information may be collected automatically.
              </p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-700 font-medium">
                Note: As stated, we strictly limit automated collection: <strong>we may only and only collect your Device ID</strong> and standard technical metadata necessary for ad serving, diagnostic crash reports, and app performance.
              </div>
              <p className="font-medium text-slate-700">This technical information may include:</p>
              <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {[
                  "Device ID and hardware model information",
                  "Operating system and Android version",
                  "App version and build number",
                  "Language and general device display settings",
                  "IP address (for coarse geographic ad compliance)",
                  "Advertising identifier (resettable by user)",
                  "Basic app interactions (e.g. session start/duration)",
                  "Crash reports and diagnostic logs (Crashlytics)",
                  "Audio engine performance telemetry",
                ].map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2.5 rounded-xl border border-slate-100 bg-slate-50/70 p-3 text-sm text-slate-700"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-slate-500 italic">
                This technical data is processed strictly by third-party services used for advertising, analytics, diagnostics, security, or app performance.
              </p>
            </div>
          </article>

          {/* Section 3 */}
          <article
            id="advertising"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                3
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Advertising
              </h2>
            </div>
            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>The App may display advertisements.</p>
              <p>
                If advertising services such as Google AdMob are used, Google and its advertising partners may collect or process information such as advertising identifiers, IP addresses, device information, ad interactions, and other information necessary to provide, measure, personalize, and protect advertising services.
              </p>
              <p>
                Depending on your location, device settings, consent choices, age, and applicable laws, advertisements may be personalized or non-personalized.
              </p>
              <div className="mt-4 space-y-2 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <p className="font-semibold text-blue-950">Learn more about Google advertising practices:</p>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                  <a
                    href="https://policies.google.com/privacy?utm_source=chatgpt.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-medium text-blue-700 underline decoration-blue-300 transition hover:text-blue-900"
                  >
                    <span>Google Privacy Policy</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <span className="hidden text-slate-300 sm:inline">&bull;</span>
                  <a
                    href="https://policies.google.com/technologies/partner-sites?utm_source=chatgpt.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 font-medium text-blue-700 underline decoration-blue-300 transition hover:text-blue-900"
                  >
                    <span>How Google uses information from sites or apps that use its services</span>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </article>

          {/* Section 4 */}
          <article
            id="analytics"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                4
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Analytics and Crash Reporting
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                We may use analytics, performance monitoring, or crash-reporting services to understand how the App performs, identify technical problems, and improve stability and functionality.
              </p>
              <p>
                These services may process information such as device information, app interactions, crash logs, diagnostic data, and performance information.
              </p>
              <p>
                If the App uses services such as Google Analytics for Firebase, Firebase Crashlytics, or similar services, information may be processed according to the applicable service provider&apos;s privacy policies.
              </p>
            </div>
          </article>

          {/* Section 5 */}
          <article
            id="permissions"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                5
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Permissions
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                The App may request Android permissions that are necessary to provide specific functionality.
              </p>
              <p>
                Permissions are only requested when required by a feature of the App. The exact permissions available may depend on the App version and Android version.
              </p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="font-medium text-slate-800">
                  We do not use device permissions to access personal information for purposes unrelated to the functionality disclosed to users.
                </p>
              </div>
            </div>
          </article>

          {/* Section 6 */}
          <article
            id="piano-features"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                6
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Piano, Music and Audio Features
              </h2>
            </div>
            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                The App incorporates rich audio synthesis, low-latency performance tools, and educational music features, all of which run locally on your device:
              </p>
              
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                  <h4 className="font-semibold text-slate-800 text-sm">🎹 Piano Keyboard &amp; Touch Mechanics</h4>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    Zero-latency multi-touch engine supporting full polyphonic chords, continuous glissando finger slides, customizable key sizing (40px to 80px), key note/octave labels, and acoustic sustain resonance pedal simulation.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                  <h4 className="font-semibold text-slate-800 text-sm">🔊 Hardware-Accelerated SoundPool</h4>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    48 concurrent hardware audio streams (&lt;5ms latency) preloading real instrument tone libraries: Steinway Concert Grand Piano, Jazz Saxophone &amp; Horns, Steel-String Acoustic Guitar, Woodwind Flute, Polyphonic Synth, Strings Ensemble, Electric Guitar, and Music Box.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                  <h4 className="font-semibold text-slate-800 text-sm">🥁 Cyber Drum Stage &amp; Sequencer</h4>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    Standalone drum machine with 40 studio percussion pads (Acoustic &amp; 808 Snares, Kicks, Hi-Hats, Cymbals, Toms, Bongos, Congas, Latin percussion) and a 16-step programmable beat sequencer with BPM slider.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3.5">
                  <h4 className="font-semibold text-slate-800 text-sm">🎵 Rhythm Song Quest &amp; Studio Metronome</h4>
                  <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                    Interactive song practice with illuminated guided keys for famous classical and popular masterpieces, auto-play demonstrations, high-score streak multiplier, and high-precision metronome (40 to 240 BPM).
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-4">
                <p className="font-semibold text-indigo-950">
                  100% Local Real-Time Audio Processing:
                </p>
                <p className="mt-1 text-sm text-indigo-900 leading-relaxed">
                  All audio generation, note triggers, song learning algorithms, metronome clicks, and beat sequencer rhythms are synthesized entirely on your device via hardware memory. We do <strong>not</strong> record your microphone or audio output, nor do we upload, transmit, or store any played musical compositions or MIDI sequences to any remote server.
                </p>
              </div>
            </div>
          </article>

          {/* Section 7 */}
          <article
            id="childrens-privacy"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                7
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Children&apos;s Privacy
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                The App is a musical application that may appeal to users of different ages.
              </p>
              <p>
                We do not knowingly request children to provide personal information directly to us.
              </p>
              <p>
                If the App is directed to children or includes children in its selected Google Play target audience, additional Google Play Families requirements and applicable children&apos;s privacy laws may apply, particularly to advertising and third-party SDKs.
              </p>
              <p>
                If you believe that a child has provided personal information to us improperly, please contact us using the contact information below.
              </p>
            </div>
          </article>

          {/* Section 8 */}
          <article
            id="third-party-services"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                8
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Third-Party Services
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                The App may use third-party services for purposes such as advertising, analytics, crash reporting, app distribution, and technical functionality.
              </p>
              <p>
                These third parties may process information according to their own privacy policies and terms.
              </p>
              <p>
                Depending on the final App configuration, third-party services may include Google Play services, Google AdMob, Firebase, or other services disclosed within the App or this Privacy Policy.
              </p>
            </div>
          </article>

          {/* Section 9 */}
          <article
            id="how-info-used"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                9
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                How Information Is Used
              </h2>
            </div>
            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>Information processed through the App or its third-party service providers may be used to:</p>
              <ul className="space-y-2">
                {[
                  "Provide and maintain App functionality",
                  "Deliver and measure advertisements",
                  "Diagnose crashes and technical problems",
                  "Monitor performance and stability",
                  "Improve features and user experience",
                  "Prevent fraud, abuse, or security threats",
                  "Comply with legal obligations",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-600"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-amber-950 font-medium">
                We do not sell personal information directly to third parties for monetary payment.
              </div>
            </div>
          </article>

          {/* Section 10 */}
          <article
            id="data-sharing"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                10
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Data Sharing
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                Information may be shared or processed by service providers when necessary to operate services integrated into the App, including advertising, analytics, diagnostics, hosting, security, and app distribution services.
              </p>
              <p>
                Information may also be disclosed when required by applicable law, regulation, legal process, or valid governmental request.
              </p>
            </div>
          </article>

          {/* Section 11 */}
          <article
            id="data-retention"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                11
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Data Retention
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                Information processed directly by us, if any, is retained only for as long as reasonably necessary for the purposes described in this Privacy Policy or as required by law.
              </p>
              <p>
                Information processed by third-party services is subject to their respective retention policies.
              </p>
            </div>
          </article>

          {/* Section 12 */}
          <article
            id="data-security"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                12
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Data Security
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                We take reasonable measures to protect information associated with the App.
              </p>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-700">
                  However, no electronic transmission, device, or storage system can be guaranteed to be completely secure.
                </p>
              </div>
            </div>
          </article>

          {/* Section 13 */}
          <article
            id="privacy-choices"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                13
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Your Privacy Choices
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                Depending on your device, location, and applicable law, you may have options to control certain data processing.
              </p>
              <p>
                These may include resetting or restricting your advertising identifier, controlling advertising personalization, managing Android permissions, clearing App data, or uninstalling the App.
              </p>
              <p>
                Where legally required, appropriate consent choices may also be presented for advertising or other data processing.
              </p>
            </div>
          </article>

          {/* Section 14 */}
          <article
            id="data-deletion"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                14
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Data Deletion
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                If the App does not create user accounts or store personal information on our own servers, there may be no user account data for us to delete.
              </p>
              <p>
                You can remove locally stored App information by clearing the App&apos;s data through Android settings or uninstalling the App.
              </p>
              <p>
                If you have contacted us or believe we hold personal information about you and would like to request deletion, contact us at the email address below.
              </p>
            </div>
          </article>

          {/* Section 15 */}
          <article
            id="international-processing"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                15
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                International Processing
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                Third-party service providers used by the App may process information on servers located in countries other than your country of residence.
              </p>
              <p>
                Where applicable, such processing is governed by the service provider&apos;s privacy practices and applicable data-protection requirements.
              </p>
            </div>
          </article>

          {/* Section 16 */}
          <article
            id="policy-changes"
            className="scroll-mt-20 rounded-2xl border border-slate-200/90 bg-white p-6 shadow-sm transition hover:shadow-md sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                16
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Changes to This Privacy Policy
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-slate-600 leading-relaxed">
              <p>
                We may update this Privacy Policy periodically to reflect changes to the App, third-party services, legal requirements, or our privacy practices.
              </p>
              <p>
                When changes are made, the updated Privacy Policy will be published with a revised &ldquo;Last Updated&rdquo; date.
              </p>
              <p>
                We encourage users to review this Privacy Policy periodically.
              </p>
            </div>
          </article>

          {/* Section 17 */}
          <article
            id="contact-us"
            className="scroll-mt-20 rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/70 via-white to-slate-50 p-6 shadow-sm sm:p-8"
          >
            <div className="flex items-center gap-3 border-b border-indigo-100 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm font-bold text-white">
                17
              </span>
              <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">
                Contact Us
              </h2>
            </div>
            <div className="mt-4 space-y-4 text-slate-600 leading-relaxed">
              <p>
                If you have questions, concerns, or requests regarding this Privacy Policy or the App&apos;s privacy practices, contact us at:
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <a
                  href="mailto:jkjitendra4292@gmail.com"
                  className="flex items-center gap-3.5 rounded-xl border border-indigo-200/80 bg-white p-4 shadow-sm transition hover:border-indigo-400 hover:shadow-md"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email Us</p>
                    <p className="truncate font-semibold text-indigo-700">jkjitendra4292@gmail.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-3.5 rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Jurisdiction / Location</p>
                    <p className="font-semibold text-slate-800">India</p>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>

        {/* Footer Bar */}
        <footer className="mt-12 border-t border-slate-200 pt-8 text-center text-sm text-slate-500">
          <p>
            &copy; {new Date().getFullYear()} Piano: Keyboard, Learn &amp; Songs. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/" className="text-indigo-600 hover:underline">
              Home
            </Link>
            <span>&bull;</span>
            <a href="#info-we-collect" className="text-indigo-600 hover:underline">
              Back to Top
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}
